import { Box, Card, TextField, Typography } from "@mui/material"
import { Sparkles } from "lucide-react"
import { InlineAiGenerator } from "@crm-src/components/inline-ai-generator"
import { AiTextImprover } from "@crm-src/components/ai-text-improver"
import {
  BROCHURE_COLORS,
  BROCHURE_FONT,
  XOP_BRAND_MARK_URL,
  escapeHtml,
  showcaseImageOptions,
  toAbsoluteUrl,
  toPreviewImageUrl,
  type UseCaseSection,
} from "./model"

// Editor for the hero: a single photo, a kicker, one title, and a subtitle. The
// rendered email puts a slim brand strip + the photo + a navy title band — ONE
// focal unit (the photo), ONE title, and no competing badge.
interface HeroSectionProps {
  section: UseCaseSection
  onUpdate: (sectionId: string, partial: any) => void
}

export function HeroSection({ section, onUpdate }: HeroSectionProps) {
  const c = section.content
  return (
    <Card sx={{ borderRadius: 0 }} style={{ marginBottom: 0 }}>
      <Box sx={{ p: 3 }}>
        <Typography component="span" sx={{ fontSize: "1.125rem", fontWeight: 600, display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
          <Sparkles size={20} />
          Hero
        </Typography>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Box>
            <Typography component="label" sx={{ fontSize: 14, fontWeight: 500 }}>Hero image</Typography>
            <Box sx={{ mt: 1 }}>
              <Box
                component="img"
                src={toPreviewImageUrl(c.imageUrl) + "?w=600&h=220&fit=crop"}
                alt="Hero"
                sx={{ width: "100%", height: 140, objectFit: "cover", borderRadius: 1, mb: 1.5, display: "block" }}
              />
              <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 1 }}>
                {showcaseImageOptions.map((img) => (
                  <Box
                    component="img"
                    key={img.id}
                    src={toPreviewImageUrl(img.url) + "?w=150&h=100&fit=crop"}
                    alt={img.name}
                    onClick={() => onUpdate(section.id, { imageUrl: img.url })}
                    sx={{
                      width: "100%", height: 56, objectFit: "cover", borderRadius: 1, cursor: "pointer",
                      border: "2px solid",
                      borderColor: c.imageUrl === img.url ? "primary.main" : "divider",
                    }}
                  />
                ))}
              </Box>
            </Box>
          </Box>

          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.5 }}>
            <Box>
              <Typography component="label" htmlFor={`uc-eyebrow-${section.id}`} sx={{ fontSize: 14, fontWeight: 500 }}>Eyebrow</Typography>
              <TextField
                fullWidth size="small" id={`uc-eyebrow-${section.id}`}
                value={c.eyebrow || ""}
                onChange={(e) => onUpdate(section.id, { eyebrow: e.target.value })}
                placeholder="e.g., Beyond the hotel room"
              />
            </Box>
            <Box>
              <Typography component="label" htmlFor={`uc-brandtag-${section.id}`} sx={{ fontSize: 14, fontWeight: 500 }}>Brand tag</Typography>
              <TextField
                fullWidth size="small" id={`uc-brandtag-${section.id}`}
                value={c.brandTag || ""}
                onChange={(e) => onUpdate(section.id, { brandTag: e.target.value })}
                placeholder="e.g., Mass Notification"
                helperText="Small label in the brand strip"
              />
            </Box>
          </Box>

          <Box>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
              <Typography component="label" htmlFor={`uc-title-${section.id}`} sx={{ fontSize: 14, fontWeight: 500 }}>Title</Typography>
              <Box sx={{ display: "flex", gap: 1 }}>
                <InlineAiGenerator
                  fieldType="short"
                  context="use-case brochure hero title — a clear headline naming the problem or offering"
                  placeholder="e.g., name the topic"
                  currentValue={c.title}
                  onGenerate={(v) => onUpdate(section.id, { title: v })}
                />
                <AiTextImprover fieldType="short" context="use-case brochure hero title" currentText={c.title} onImprove={(v) => onUpdate(section.id, { title: v })} />
              </Box>
            </Box>
            <TextField
              fullWidth size="small" id={`uc-title-${section.id}`}
              value={c.title || ""}
              onChange={(e) => onUpdate(section.id, { title: e.target.value })}
              placeholder="Title"
            />
          </Box>

          <Box>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
              <Typography component="label" htmlFor={`uc-sub-${section.id}`} sx={{ fontSize: 14, fontWeight: 500 }}>Subtitle</Typography>
              <AiTextImprover fieldType="long" context="use-case brochure hero subtitle" currentText={c.subtitle} onImprove={(v) => onUpdate(section.id, { subtitle: v })} />
            </Box>
            <TextField
              fullWidth multiline minRows={2} size="small" id={`uc-sub-${section.id}`}
              value={c.subtitle || ""}
              onChange={(e) => onUpdate(section.id, { subtitle: e.target.value })}
              placeholder="One line that frames the story"
            />
          </Box>
        </Box>
      </Box>
    </Card>
  )
}

// Serialize the hero: a slim navy brand strip, the full-width photo (with HTML
// width/height attrs for Outlook), then a navy title band (kicker · title ·
// lime rule · subtitle). One photo focal point; one title; no floating badge.
export function heroToHtml(section: UseCaseSection, tenantName: string): string {
  const c = section.content || {}
  const brandMark = toAbsoluteUrl(XOP_BRAND_MARK_URL)
  const image = c.imageUrl ? toAbsoluteUrl(c.imageUrl) : ""
  const eyebrow = c.eyebrow ? escapeHtml(c.eyebrow) : ""
  const title = escapeHtml(c.title || "")
  const subtitle = c.subtitle ? escapeHtml(c.subtitle) : ""
  // A small product/category label (e.g. "Mass Notification") that rides in the
  // brand strip — a restrained tag, NOT a competing badge/pill next to the title.
  const brandTag = c.brandTag ? escapeHtml(c.brandTag) : ""

  return `
          <!-- Brand strip -->
          <tr>
            <td bgcolor="${BROCHURE_COLORS.navyDeep}" class="mobile-pad" style="background-color: ${BROCHURE_COLORS.navyDeep}; padding: 15px 40px;">
              <table cellpadding="0" cellspacing="0" border="0" role="presentation">
                <tr>
                  <td style="vertical-align: middle; padding-right: 12px;" valign="middle">
                    <img src="${brandMark}" alt="${escapeHtml(tenantName)}" width="30" height="30" style="display: block; border: 0; width: 30px; height: 30px;" />
                  </td>
                  <td style="vertical-align: middle;" valign="middle">
                    <span style="color: ${BROCHURE_COLORS.white}; font-family: ${BROCHURE_FONT}; font-size: 15px; font-weight: bold; letter-spacing: 0.3px;">${escapeHtml(tenantName)}</span>
                  </td>
                  ${brandTag ? `
                  <td style="vertical-align: middle; padding-left: 12px;" valign="middle">
                    <span style="color: ${BROCHURE_COLORS.lime}; font-family: ${BROCHURE_FONT}; font-size: 10px; font-weight: bold; letter-spacing: 2px; text-transform: uppercase;">${brandTag}</span>
                  </td>
                  ` : ''}
                </tr>
              </table>
            </td>
          </tr>
          ${image ? `
          <!-- Hero photo -->
          <tr>
            <td style="padding: 0; font-size: 0; line-height: 0;">
              <img src="${image}" alt="${title || 'Hero'}" width="640" style="width: 100%; max-width: 640px; height: auto; display: block; border: 0;" />
            </td>
          </tr>
          ` : ''}
          <!-- Title band -->
          <tr>
            <td bgcolor="${BROCHURE_COLORS.navy}" class="mobile-pad" style="background-color: ${BROCHURE_COLORS.navy}; padding: 40px 40px 44px 40px;">
              ${eyebrow ? `<p style="margin: 0 0 12px 0; color: ${BROCHURE_COLORS.lime}; font-family: ${BROCHURE_FONT}; font-size: 12px; font-weight: bold; letter-spacing: 2px; text-transform: uppercase;">${eyebrow}</p>` : ''}
              <h1 style="margin: 0; color: ${BROCHURE_COLORS.white}; font-family: ${BROCHURE_FONT}; font-size: 30px; line-height: 37px; font-weight: bold;">${title}</h1>
              <div style="width: 56px; height: 4px; background-color: ${BROCHURE_COLORS.lime}; border-radius: 2px; margin: 16px 0 0 0; font-size: 0; line-height: 0;">&nbsp;</div>
              ${subtitle ? `<p style="margin: 16px 0 0 0; color: #C7D0DE; font-family: ${BROCHURE_FONT}; font-size: 16px; line-height: 24px;">${subtitle}</p>` : ''}
            </td>
          </tr>
          `
}
